import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdLogout, MdOutlineFullscreen, MdOutlineFullscreenExit } from "react-icons/md";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import logo from "../../assets/blue_logo.svg";
import DevelopmentEnvironmentsIcon from "../../assets/DevelopmentEnvironmentsWhite.svg";
import LearningAreaIcon from "../../assets/learningArea.svg";
import QuizzesHomeworksIcon from "../../assets/QuizzesHomeworksWhite.svg";
import school_placeholder from "../../assets/school_placeholder.svg";
import user_placeholder from "../../assets/user-profile.jpg";
import { USER_TYPE } from "../../constants/common";
import useAuth from "../../hooks/useAuth";
import { clearUser } from "../../redux/slices/userSlice";
import authServices from "../../services/authServices";
import studentServices from "../../services/studentServices";
import { getLanguage } from "../../utils/getLanguage";
import { useNavigateWithLang } from "../../utils/navigation";
import Button from "../Button";
import LanguageSwitcher from "../LanguageSwitcher";
import NotificationDropdown from "../NotificationDropdown";

const StudentHeader = () => {
  const user = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigateWithLang();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const lang = getLanguage();

  const [courseDetails, setCourseDetails] = useState(null);
  const [activeTab, setActiveTab] = useState("coding");
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

  const TABS =
    lang === "ar"
      ? [
          { key: "coding-area", label: "coding_area", icon: DevelopmentEnvironmentsIcon },
          { key: "learning-area", label: "learning_area", icon: LearningAreaIcon },
        ]
      : [
          { key: "learning-area", label: "learning_area", icon: LearningAreaIcon },
          { key: "coding-area", label: "coding_area", icon: DevelopmentEnvironmentsIcon },
        ];

  useEffect(() => {
    fetchCourseDetails();
  }, []);

  useEffect(() => {
    const matchedTab = TABS.find((tab) => tab.key && pathname.includes(tab.key));
    if (matchedTab) {
      setActiveTab(matchedTab.key);
    } else {
      setActiveTab("");
    }
  }, [pathname]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    const handleKeyDown = (e) => {
      if (e.key === "Escape") document.exitFullscreen().catch(console.error);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error enabling fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen().catch((err) => {
        console.error(`Error exiting fullscreen: ${err.message}`);
      });
    }
  };

  const handleSignOut = async () => {
    await authServices.logout();
    dispatch(clearUser());
    if (user?.is_b12) {
      const logoutUrl = `https://k12.b12app.com/connect/endsession?id_token_hint=${user?.id_token}`;
      window.location.href = logoutUrl;
    }
    navigate("/login");
  };

  const renderTabs = () => {
    const filteredTabs =
      courseDetails?.for_children === true ? TABS.filter((tab) => tab.key !== "coding-area") : TABS;

    return (
      <div className="flex justify-center gap-1 overflow-x-auto px-4 sm:gap-3 rtl:flex-row-reverse">
        {filteredTabs.map((tab) => {
          const isActive = tab.key === activeTab;
          const isCodingAndActive = tab.key === "coding-area" && isActive;

          // Add responsive class to hide coding-area on mobile
          const responsiveClass = tab.key === "coding-area" ? "hidden sm:flex" : "flex";

          return (
            <button
              key={tab.key}
              onClick={
                isCodingAndActive
                  ? undefined
                  : () => {
                      setActiveTab(tab.key);
                      navigate(`/${USER_TYPE.STUDENT.ROLE}/${tab.key}`);
                    }
              }
              className={`${responsiveClass} items-center gap-2 whitespace-nowrap rounded-b-3xl bg-blue-500 px-4 font-semibold transition-all sm:px-10 ${
                isActive
                  ? "h-20 rounded-b-3xl !font-bold text-white shadow-[rgba(0,_0,_0,_0.40)_0px_0px_14px]"
                  : "!h-[68px] !font-light text-white hover:!h-20 hover:rounded-b-3xl hover:shadow-[rgba(0,_0,_0,_0.40)_0px_0px_14px]"
              }`}
            >
              <img src={tab.icon} className="hidden h-4 w-4 sm:block" alt="" />
              <span>{t(tab.label)}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderUserAvatar = () => (
    <img
      src={user.img_uri || user_placeholder}
      alt="User Avatar"
      className="h-8 w-8 rounded-full border-4 border-blue-300 object-cover"
    />
  );

  const fetchCourseDetails = async () => {
    try {
      const response = await studentServices.getStudentCourses();
      setCourseDetails(response);
    } catch (error) {
      console.error("Failed to fetch course details:", error);
    }
  };

  return (
    <>
      {/* Header */}
      <header className="relative bg-blue-500 py-1 shadow-md md:mb-8">
        <div className="mx-auto flex items-center justify-between px-4 py-1 md:px-10">
          {/* Logo */}
          <div
            onClick={() => navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area`)}
            className="flex cursor-pointer items-center"
          >
            <img src={logo} alt="Logo" />
          </div>

          {/* Tabs inside header (desktop only) */}
          {courseDetails && (
            <div className="absolute left-1/2 top-8 z-20 hidden -translate-x-1/2 -translate-y-1/2 md:block">
              {renderTabs()}
            </div>
          )}

          {/* Action Icons */}
          <div className="flex items-center space-x-4 text-white">
            <Button
              onClick={toggleFullscreen}
              icon={
                isFullscreen ? (
                  <MdOutlineFullscreenExit className="text-white" size={20} />
                ) : (
                  <MdOutlineFullscreen className="text-white" size={20} />
                )
              }
            />
            <NotificationDropdown />

            <LanguageSwitcher isWhite />

            <Menu
              menuButton={
                <MenuButton>
                  <div className="flex items-center space-x-2 rounded-lg px-1">{renderUserAvatar()}</div>
                </MenuButton>
              }
              transition
              align="end"
              menuClassName="rounded-lg p-1 bg-white dark:border-gray-700 dark:bg-[#2C3A4A]"
            >
              <MenuItem
                onClick={() => navigate(`/${USER_TYPE.STUDENT.ROLE}/profile`)}
                className="cursor-pointer px-2 text-xs font-semibold text-black"
              >
                {user?.username || user?.b12_email}
              </MenuItem>
              <MenuItem
                onClick={handleSignOut}
                className="flex items-center gap-2 rounded-lg px-2 py-2 text-xs"
              >
                <MdLogout size={16} />
                {t("logout")}
              </MenuItem>
            </Menu>

            <div className="flex items-center">
              <img
                src={user?.school?.image || school_placeholder}
                alt="School Avatar"
                className="h-8 w-8 rounded-full border-4 border-blue-300 object-cover"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Tabs below header (mobile only) */}
      {courseDetails && <div className="mb-4 block md:hidden">{renderTabs()}</div>}
    </>
  );
};

export default StudentHeader;
